import { cx } from '@emotion/css'
import {
    BreakpointRatios,
    CropBreakpointSet,
    FixedRatio,
    ImageCrop,
    ImageSource,
} from '@news-mono/web-common'
import React from 'react'
import {
    ShouldLoadProps,
    useLazyLoading,
} from '../../__helpers/with-lazy-loading'
import { StyledNoScriptImage } from './Image.noscript'
import { Image } from './Image.web'
import { PictureWithIntrinsicRatio } from './Picture.styled'
import { ResponsivePictureSizes } from './responsive'
import { getPictureSources, isLazyLoadGloballyDisabled } from './utils'

export interface ImageSrcInfo {
    srcCallback: (src: string, width: number) => string
    initialWidth: number
}

interface LazyLoadingPictureProps {
    disableLazyLoad?: boolean

    /** Additional picture styles */
    pictureClassName?: string

    /** Additional img styles */
    imageClassName?: string

    image: ImageSource
    srcInfo: ImageSrcInfo
    imageSizes: ResponsivePictureSizes

    fixedRatio: FixedRatio | BreakpointRatios
    breakpointCrops: CropBreakpointSet<ImageCrop>
}

/**
 * This component renders an entire picture component including source sets and noscript fallbacks
 */
export const LazyLoadingPicture: React.FC<LazyLoadingPictureProps> = ({
    pictureClassName,
    imageClassName,
    image,
    srcInfo,
    imageSizes,
    fixedRatio,
    breakpointCrops,
    disableLazyLoad = false,
}) => {
    const { lazyLoadRef, lazyLoadHasTriggered, wasLazyLoaded } =
        useLazyLoading<HTMLImageElement>(shouldLoad)
    const show =
        lazyLoadHasTriggered ||
        isLazyLoadGloballyDisabled() ||
        disableLazyLoad ||
        false

    return (
        <PictureWithIntrinsicRatio
            ref={lazyLoadRef}
            image={image}
            fixedRatio={fixedRatio}
            className={cx(pictureClassName, 'image')}
        >
            {show && (
                <LazyImageWithSources
                    image={image}
                    breakpointCrops={breakpointCrops}
                    imageSizes={imageSizes}
                    srcInfo={srcInfo}
                    disableLazyLoad={disableLazyLoad}
                    wasLazyLoaded={wasLazyLoaded}
                    className={imageClassName}
                />
            )}
        </PictureWithIntrinsicRatio>
    )
}

interface LazyImageWithSourcesProps {
    disableLazyLoad: boolean
    breakpointCrops: CropBreakpointSet<ImageCrop>
    imageSizes: ResponsivePictureSizes
    srcInfo: ImageSrcInfo
    image: ImageSource
    wasLazyLoaded: boolean
    className?: string
}

const LazyImageWithSources: React.FC<LazyImageWithSourcesProps> = ({
    disableLazyLoad,
    breakpointCrops,
    imageSizes,
    srcInfo,
    image,
    className,
    wasLazyLoaded,
}) => {
    const pictureSources = getPictureSources(
        breakpointCrops,
        imageSizes,
        srcInfo,
    )
    const defaultSrc = srcInfo.srcCallback(
        breakpointCrops.default.reference,
        srcInfo.initialWidth,
    )

    return (
        <React.Fragment>
            {pictureSources}

            <Image
                enableFadeIn={wasLazyLoaded}
                src={defaultSrc}
                alt={image.altText}
                className={className}
                // Leverage in browser lazy loading to reduce the number of images
                // loaded on mobile viewports in browsers supporting it
                loading={disableLazyLoad ? undefined : 'lazy'}
            />

            {!disableLazyLoad && (
                <StyledNoScriptImage alt={image.altText} src={defaultSrc} />
            )}
        </React.Fragment>
    )
}

function shouldLoad(props: ShouldLoadProps) {
    if (props.percentVisible > 0) return true
    return (
        !!props.distanceFromViewport &&
        !!props.viewportHeight &&
        // Is within one screen away
        props.distanceFromViewport / props.viewportHeight < 1
    )
}
